// Please do not use optional chaining (?.), nullish coalescing (??), or other ES2020+ features

import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import { Box, Typography, TextField, Switch, FormControlLabel, Checkbox, IconButton, Collapse } from '@mui/material';
import { styled } from '@mui/material/styles';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import SettingsIcon from '@mui/icons-material/Settings';
import OntologyGraphWrapper from './Graph/components/OntologyGraphWrapper';
import Events from './components/Events';
import RealAgentChat from './components/RealAgentChat';
import CodeGraphPanel from './components/CodeGraphPanel';
import { setUrlParameter, removeUrlParameter } from '../../utils';
import { useDragResize, useDebouncedUrlSync } from './hooks';

const ResizableBox = styled(Box)({
  position: 'relative',
});

const VerticalDragHandle = styled(Box)({
  position: 'absolute',
  top: 0,
  right: 0,
  width: '4px',
  height: '100%',
  cursor: 'col-resize',
  transition: 'background-color 0.2s ease',
  zIndex: 1000,
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.15)',
  },
});

const HorizontalDragHandle = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '4px',
  cursor: 'row-resize',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.15)',
  },
});

const ContactIdTextField = styled(TextField)({
    '& .MuiInputBase-root': {
        height: '24px',
        marginTop: '2px',
        backgroundColor: '#1E1E1E',
        color: 'white',
        '&:hover': {
            backgroundColor: '#2D2D2D',
        },
    },
    '& .MuiInputBase-input': {
        padding: '4px 8px',
        fontSize: '14px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.1)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.3)',
    },
});

// Memoize the graph box content
const GraphContent = memo(() => (
  <Box sx={{ 
    flex: 1,
    height: '100%',
    background: '#000000',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
  }}>
    <Box sx={{ p: '12px' }}>
      <Typography variant="h5" sx={{ color: 'white' }}>
        Graph
      </Typography>
    </Box>
    <Box sx={{ 
      flex: 1,
      overflow: 'hidden',
      transition: 'opacity 0.3s ease',
    }}>
      <OntologyGraphWrapper/>
    </Box>
  </Box>
));

GraphContent.displayName = 'GraphContent';

// Memoize the events box content
const EventsContent = memo(({ eventsHeight, isDraggingHorizontal, onDragHandleMouseDown, resizableRef }) => (
  <ResizableBox
    ref={resizableRef}
    sx={{ 
      height: eventsHeight,
      minHeight: '50px',
      maxHeight: 'calc(100vh - 300px)',
      overflow: 'hidden',
      transition: isDraggingHorizontal ? 'none' : 'height 0.2s ease',
      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
      background: 'rgba(43, 45, 58, 1)',
      backdropFilter: 'blur(10px)',
      borderRadius: '12px 12px 0 0',
      margin: '0 12px',
    }}
  >
    <HorizontalDragHandle onMouseDown={onDragHandleMouseDown}>
      <DragHandleIcon />
    </HorizontalDragHandle>
    <Box sx={{ 
      p: '10px 5px 0 12px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      background: 'rgba(0, 0, 0, 0.91)',
    }}>
      <Typography variant="h5" sx={{ color: 'white', mb: 1 }}>
        Events
      </Typography>
    </Box>
    <Box sx={{ 
      height: 'calc(100% - 60px)',
      background: 'rgba(0, 0, 0, 0.2)',
      borderRadius: '0 0 12px 12px',
    }}>
      <Events/>
    </Box>
  </ResizableBox>
));

EventsContent.displayName = 'EventsContent';

// Graph Settings Panel Component
const GraphSettingsPanel = ({ settings, onSettingsChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Ensure settings has all required properties with defaults
  const safeSettings = {
    showIsaLabels: true,
    showAkoLabels: true,
    monochromeView: false,
    showUidData: true,
    ...settings
  };

  return (
    <Box sx={{ 
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      gap: 1,
    }}>
      <IconButton
        size="small"
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{
          color: '#ffffff',
          padding: '2px',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        <SettingsIcon sx={{ fontSize: '16px' }} />
      </IconButton>
      
      <Collapse in={isExpanded} orientation="horizontal">
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1.5,
          padding: '2px 6px',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '4px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          marginLeft: '4px',
        }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={Boolean(safeSettings.showIsaLabels)}
                onChange={(e) => onSettingsChange('showIsaLabels', e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#347CFF',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#347CFF',
                  },
                }}
              />
            }
            label={
              <Typography sx={{ 
                color: '#ffffff', 
                fontSize: '11px',
                fontWeight: 500,
              }}>
                ISA
              </Typography>
            }
            sx={{ margin: 0 }}
          />
          
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={Boolean(safeSettings.showAkoLabels)}
                onChange={(e) => onSettingsChange('showAkoLabels', e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#347CFF',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#347CFF',
                  },
                }}
              />
            }
            label={
              <Typography sx={{ 
                color: '#ffffff', 
                fontSize: '11px',
                fontWeight: 500,
              }}>
                AKO
              </Typography>
            }
            sx={{ margin: 0 }}
          />
          
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={Boolean(safeSettings.monochromeView)}
                onChange={(e) => onSettingsChange('monochromeView', e.target.checked)}
                sx={{
                  color: '#347CFF',
                  '&.Mui-checked': {
                    color: '#347CFF',
                  },
                  padding: '2px',
                }}
              />
            }
            label={
              <Typography sx={{ 
                color: '#ffffff', 
                fontSize: '11px',
                fontWeight: 500,
              }}>
                Mono
              </Typography>
            }
            sx={{ margin: 0 }}
          />
          
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={Boolean(safeSettings.showUidData)}
                onChange={(e) => onSettingsChange('showUidData', e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#347CFF',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#347CFF',
                  },
                }}
              />
            }
            label={
              <Typography sx={{ 
                color: '#ffffff', 
                fontSize: '11px',
                fontWeight: 500,
              }}>
                UID/ID
              </Typography>
            }
            sx={{ margin: 0 }}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

const AgentOntologyPage = () => {
  const [contactId, setContactId] = useState('');
  const [debouncedContactId, setDebouncedContactId] = useState('');
  
  const [eventsHeight, setEventsHeight] = useState('20vh');
  const [chatWidth, setChatWidth] = useState('40%');
  
  // Code panel state with localStorage persistence
  const [isCodePanelOpen, setIsCodePanelOpen] = useState(() => {
    const saved = localStorage.getItem('codePanelOpen');
    return saved ? JSON.parse(saved) : false;
  });
  const [codePanelWidth, setCodePanelWidth] = useState(() => {
    const saved = localStorage.getItem('codePanelWidth');
    return saved ? parseInt(saved, 10) : 400;
  });
  const [graphData, setGraphData] = useState({ concepts: [], individuals: [] });
  
  // Graph settings state with localStorage persistence
  const [graphSettings, setGraphSettings] = useState(() => {
    const saved = localStorage.getItem('graphSettings');
    return saved ? JSON.parse(saved) : {
      showIsaLabels: true,
      showAkoLabels: true,
      monochromeView: false,
      showUidData: true,
    };
  });
  
  const resizableRef = useRef(null);
  const chatBoxRef = useRef(null);
  const graphWrapperRef = useRef(null);
  const eventsWrapperRef = useRef(null);

  // Drag/resize logic using custom hook
  const {
    isDraggingVertical,
    isDraggingHorizontal,
    handleVerticalMouseDown,
    handleHorizontalMouseDown,
  } = useDragResize({
    onVerticalResize: setChatWidth,
    onHorizontalResize: setEventsHeight,
    minVertical: 0.2,
    maxVertical: 0.8,
    minHorizontal: 50,
    maxHorizontal: 0.6,
    getVerticalRef: () => chatBoxRef,
    getHorizontalRef: () => resizableRef,
  });

  // Debounced URL sync using custom hook
  useDebouncedUrlSync({
    value: contactId,
    param: 'contactId',
    setUrlParameter,
    removeUrlParameter,
    delay: 500,
  });

  // Load initial contactId from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlContactId = params.get('contactId');
    if (urlContactId) {
      setContactId(urlContactId);
      setDebouncedContactId(urlContactId);
    }
  }, []);

  // Function to get graph data from the wrapper
  const getGraphData = useCallback(() => {
    if (graphWrapperRef.current && graphWrapperRef.current.getGraphData) {
      const data = graphWrapperRef.current.getGraphData();
      
      setGraphData(data);
    } else {
      console.warn('CodeGraphPanel: graphWrapperRef.current or getGraphData not available');
    }
  }, []);

  const updateGraph = useCallback(() => {
    if (graphWrapperRef.current && graphWrapperRef.current.updateGraph) {
      graphWrapperRef.current.updateGraph();
      
      // Also update the graph data for the code panel after a short delay
      // to ensure the graph has been updated first
      setTimeout(() => {
        getGraphData();
      }, 500);
    }
  }, [getGraphData]);

  // Function to update events
  const updateEvents = useCallback(() => {
    if (eventsWrapperRef.current && eventsWrapperRef.current.getEvents) {
      eventsWrapperRef.current.getEvents();
    }
  }, []);

  // Handle code panel toggle
  const handleCodePanelToggle = useCallback(() => {
    const newState = !isCodePanelOpen;
    setIsCodePanelOpen(newState);
    localStorage.setItem('codePanelOpen', JSON.stringify(newState));
    
    // Get fresh graph data when opening
    if (newState) {
      setTimeout(() => {
        getGraphData();
      }, 100);
    }
  }, [isCodePanelOpen, getGraphData]);

  // Handle code panel width change
  const handleCodePanelWidthChange = useCallback((newWidth) => {
    setCodePanelWidth(newWidth);
    localStorage.setItem('codePanelWidth', newWidth.toString());
  }, []);

  const handleContactIdChange = (event) => {
    const newContactId = event.target.value;
    setContactId(newContactId);
  };

  // Update debounced value after typing stops
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedContactId(contactId);
    }, 500);
    return () => clearTimeout(timer);
  }, [contactId]);

  // Update graph data periodically when code panel is open
  useEffect(() => {
    if (!isCodePanelOpen) return;

    const interval = setInterval(() => {
      getGraphData();
    }, 2000); // Update every 2 seconds

    return () => clearInterval(interval);
  }, [isCodePanelOpen, getGraphData]);

  // Handle graph settings change
  const handleGraphSettingsChange = useCallback((setting, value) => {
    setGraphSettings(prev => {
      const newSettings = { ...prev, [setting]: value };
      localStorage.setItem('graphSettings', JSON.stringify(newSettings));
      return newSettings;
    });
  }, []);

  return (
    <React.Fragment>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'row',
        height: 'calc(100vh - 40px)', // Account for menu height
        bgcolor: '#1E1E1E', // VS Code-like dark theme background
        color: '#ffffff', // Light text color
      }}>
        <ResizableBox ref={chatBoxRef} sx={{ 
          width: chatWidth,
          height: '100%',
          position: 'relative',
          borderRight: '1px solid rgba(255, 255, 255, 0.1)',
          transition: isDraggingVertical ? 'none' : 'width 0.1s ease-out',
        }}>
          <Box className='chat-box' sx={{ 
            width: '100%',
            height: '100%',
            bgcolor: '#1E1E1E',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'relative',
          }}>
            <Box sx={{ 
              p: '8px 16px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}>
              <Typography variant="h6" sx={{ 
                color: '#ffffff',
                fontSize: '18px',
                fontWeight: 600,
              }}>
                Chat
              </Typography>
              <ContactIdTextField
                size="small"
                placeholder="Contact ID"
                value={contactId}
                onChange={handleContactIdChange}
                sx={{ width: '100px' }}
              />
            </Box>
            <Box sx={{
              flex: 1,
              overflow: 'hidden',
              p: '12px',
            }}>
              <RealAgentChat contactId={debouncedContactId} updateGraph={() => { updateGraph(); updateEvents(); }} />
              {/* <AgentChat 
                onMessageIndexChange={handleMessageIndexChange}
              /> */}
            </Box>
            <VerticalDragHandle onMouseDown={handleVerticalMouseDown} />
          </Box>
        </ResizableBox>

        <Box className='graph-box' sx={{ 
          flex: 1,
          height: '100%',
          bgcolor: '#1E1E1E',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <Box sx={{ 
            p: '8px 16px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}>
            <Typography variant="h6" sx={{ 
              color: '#ffffff',
              fontSize: '18px',
              fontWeight: 600,
            }}>
              Graph
            </Typography>
            <GraphSettingsPanel
              settings={graphSettings}
              onSettingsChange={handleGraphSettingsChange}
            />
          </Box>
          <Box sx={{ 
            flex: 1,
            overflow: 'hidden',
          }}>
            <OntologyGraphWrapper 
              ref={graphWrapperRef}
              graphSettings={graphSettings}
            />
          </Box>

          <ResizableBox
            ref={resizableRef}
            sx={{ 
              height: eventsHeight,
              minHeight: '50px',
              maxHeight: '50vh',
              overflow: 'hidden',
              transition: isDraggingHorizontal ? 'none' : 'height 0.1s ease-out',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              bgcolor: '#252526', // Slightly lighter than main background
              backdropFilter: 'blur(10px)',
            }}
          >
            <HorizontalDragHandle onMouseDown={handleHorizontalMouseDown} />
            <Box sx={{ 
              p: '8px 16px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              bgcolor: '#252526',
            }}>
              <Typography variant="h6" sx={{ 
                color: '#ffffff',
                fontSize: '18px',
                fontWeight: 600,
              }}>
                Events
              </Typography>
            </Box>
            <Box sx={{ 
              height: 'calc(100% - 45px)',
              bgcolor: '#252526',
            }}>
              <Events ref={eventsWrapperRef}/>
            </Box>
          </ResizableBox>
        </Box>
      </Box>

      <CodeGraphPanel
        isOpen={isCodePanelOpen}
        onToggle={handleCodePanelToggle}
        width={codePanelWidth}
        onWidthChange={handleCodePanelWidthChange}
        graphData={graphData}
        onRefresh={getGraphData}
        updateGraph={() => { updateGraph(); updateEvents(); }}
      />
    </React.Fragment>
  );
};

export default AgentOntologyPage;
